/**
 * Module dependencies.
 */

import { apiEndpoints } from 'app/core/config/api-endpoints';
import { appRoutes } from 'app/routes';
import { identity, pickBy } from 'lodash';
import { ListFilters } from 'app/components/organisms/crud/form-filters/form-filters-type';
import { TFunction } from 'react-i18next';
import { DateTime } from 'app/components/atoms/date-time/date-time';
import React from 'react';
import { normalizeTemplate } from 'app/components/organisms/crud/utils';
import { CrudListType, CrudTemplate, ListColumns } from 'app/components/organisms/crud/form-types';
import { PharmacyAuthorization } from 'app/types/pharmacy-authorization';

/**
 * List labels.
 */

const listColumns = (translate: TFunction): ListColumns<PharmacyAuthorization> => [
  {
    title: translate('common.table.columns.id'),
    dataIndex: 'userId',
    key: 'userId',
    render: item => item.user?.id
  },
  {
    title: translate('common.table.columns.name'),
    dataIndex: 'name',
    key: 'name',
    size: '1fr',
    render: item => item.user?.name || 'N/A'
  },
  {
    title: translate('common.table.columns.phoneNumber'),
    dataIndex: 'phoneNumber',
    key: 'phoneNumber',
    size: '1fr',
    render: item => item.user?.phoneNumber
  },
  {
    title: translate('common.table.columns.status'),
    dataIndex: 'status',
    key: 'status',
    render: item => item.status
  },
  {
    title: translate('common.table.columns.establishedAt'),
    dataIndex: 'establishedAt',
    key: 'establishedAt',
    render: value => (value.establishedAt ? <DateTime date={value.establishedAt} /> : 'N/A')
  },
  {
    title: translate('common.table.columns.createdAt'),
    dataIndex: 'createdAt',
    key: 'createdAt',
    render: value => <DateTime date={value.createdAt} />
  }
];

/**
 * List filters.
 */

const listFilters = (translate: TFunction): ListFilters => [
  {
    type: 'inputSearch',
    inline: true,
    name: 'search',
    label: translate('common.labels.search')
  },
  {
    type: 'selectSearch',
    inline: true,
    name: 'status',
    label: translate('common.labels.status'),
    options: [
      { label: translate('common.labels.statusLabels.established'), value: 'established' },
      { label: translate('common.labels.statusLabels.connecting'), value: 'connecting' },
      { label: translate('common.labels.statusLabels.expired'), value: 'expired' },
      { label: translate('common.labels.statusLabels.cancelled'), value: 'cancelled' },
      { label: translate('common.labels.statusLabels.blocked'), value: 'blocked' },
      { label: translate('common.labels.statusLabels.userRevoked'), value: 'revoked' }
    ]
  }
];

/**
 * List.
 */

function list(translate: TFunction): CrudListType<PharmacyAuthorization> {
  return {
    columns: listColumns(translate),
    filters: listFilters(translate),
    route: appRoutes.dashboard.services.pharmacies.sharedAccesses,
    canEdit: false,
    canRemove: false,
    resolveParams: (params: any, item: any) => {
      return {
        pharmacyId: params?.pharmacyId ?? item?.pharmacyId
      };
    },
    key: ['pharmaciesSharedAccesses'],
    normalizeFilters: (data: any) => {
      const { ...filters } = data.filters;
      const result: any = {
        filters: pickBy(filters, identity),
        order: []
      };

      return result;
    },
    normalizeData: (result: any) => {
      return result;
    },
    pagination: {
      pageSize: 25,
      pageSizeOptions: [10, 25, 50, 100, 500, 1000]
    },
    endpoint: apiEndpoints.pharmaciesAuthorizations
  };
}

/**
 * Config.
 */

export function createPharmaciesSharedAccessesTemplate(translate: TFunction): CrudTemplate<PharmacyAuthorization> {
  return normalizeTemplate(translate, {
    list: list(translate)
  });
}
